import React, { useRef, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Tooltip,
  Switch,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FontSelector from './FontSelector';

const JobBoardLeftCustomizer = ({
  settings,
  segmentHandler,
  handleSave,
  handleReset,
}) => {
  const fileInputRef = useRef(null);
  const [logoFile, setLogoFile] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    segmentHandler(name, value);
  };

  const handleSwitchChange = (e) => {
    segmentHandler('useCustomHeader', e.target.checked);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      segmentHandler('logo', URL.createObjectURL(file));
      setLogoFile(file);
    }
  };
  const handleRemoveLogo = () => {
    segmentHandler('logo', '');
    segmentHandler('logoPathUrl', '');
    setLogoFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const logoHandleReset = () => {
    segmentHandler('logo', '');
    segmentHandler('logoPathUrl', '');
    setLogoFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    handleReset();
  };

  const renderColorInputs = (title, tooltip, fields) => (
    <Box mb={3}>
      <Typography
        variant="h6"
        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
      >
        {title}
        <Tooltip title={tooltip} arrow>
          <InfoIcon fontSize="small" color="action" />
        </Tooltip>
      </Typography>
      <Grid container spacing={2}>
        {fields.map(({ label, name }) => (
          <Grid item xs={6} key={name}>
            <TextField
              label={label}
              type="color"
              name={name}
              value={settings[name]}
              onChange={handleChange}
              fullWidth
              margin="normal"
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  return (
    <Box border="1px solid #ddd" borderRadius="8px" padding={2}>
      {/* Logo Upload */}
      <Box mb={3}>
        <Typography variant="h6">Upload Logo</Typography>
        <Box
          sx={{
            border: '2px dashed #bbb',
            borderRadius: '8px',
            height: '100px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f9f9f9',
            marginBottom: 1,
            cursor: 'pointer',
            padding: 2,
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          {settings.logo ? (
            <img
              src={settings.logo}
              alt="Uploaded Logo"
              style={{ height: '100%', objectFit: 'contain' }}
            />
          ) : (
            <Typography color="textSecondary">
              No logo uploaded (Click to upload)
            </Typography>
          )}
          <input
            hidden
            ref={fileInputRef}
            accept="image/jpeg,image/jpg,image/png"
            type="file"
            onChange={handleLogoUpload}
          />
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            variant="caption"
            color="textSecondary"
            fontStyle="italic"
          >
            Accepted formats: JPG, JPEG, PNG only
          </Typography>
          {settings.logo && (
            <Typography
              variant="body2"
              color="red"
              sx={{
                cursor: 'pointer',
                textDecoration: 'underline',
                fontSize: '0.875rem',
              }}
              onClick={handleRemoveLogo}
            >
              <DeleteOutlineIcon fontSize="small" />
            </Typography>
          )}
        </Box>
      </Box>

      {/* Header Style Switch */}
      <Box mb={3}>
        <Typography variant="h6" display="flex" alignItems="center" gap={1}>
          Header Configuration
          <Tooltip title="Customize header appearance independently" arrow>
            <InfoIcon fontSize="small" color="action" />
          </Tooltip>
        </Typography>
        <Box display="flex" alignItems="center" gap={2} mt={2}>
          <Switch
            checked={settings.useCustomHeader}
            onChange={handleSwitchChange}
            className={settings.useCustomHeader ? 'activeBtn' : 'inActiveBtn'}
          />
          <Typography>Use Custom Header Style</Typography>
        </Box>

        {settings.useCustomHeader && (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Header Background"
                type="color"
                name="headerBackgroundColor"
                value={settings.headerBackgroundColor}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Header Text Color"
                type="color"
                name="headerTextColor"
                value={settings.headerTextColor}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
          </Grid>
        )}
      </Box>

      {/* Colors */}
      {renderColorInputs(
        'Background Colors',
        'Customize primary and secondary backgrounds',
        [
          { label: 'Primary Background', name: 'primaryColor' },
          { label: 'Secondary Background', name: 'secondaryColor' },
        ]
      )}

      {renderColorInputs('Font Colors', 'Customize text colors', [
        { label: 'Primary Font Color', name: 'primaryFontColor' },
        { label: 'Secondary Font Color', name: 'secondaryFontColor' },
      ])}

      {/* Font Selector */}
      <Box mb={3}>
        <Typography variant="h6" display="flex" alignItems="center" gap={1}>
          Typography
          <Tooltip title="Select font for portal" arrow>
            <InfoIcon fontSize="small" color="action" />
          </Tooltip>
        </Typography>
        <FontSelector settings={settings} segmentHandler={segmentHandler} />
      </Box>

      {/* Buttons */}
      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            mt: 2,
          }}
        >
          <Button
            variant="contained"
            onClick={logoHandleReset}
            size="medium"
            sx={{
              backgroundColor: '#3B4046',
              color: '#FFFFFF',
              textTransform: 'none',
              width: 'auto',
              borderRadius: '10px',
              padding: '4px 16px !important',
              '&:hover': {
                backgroundColor: '#3B4046',
              },
            }}
          >
            Reset
          </Button>

          <Button
            variant="contained"
            onClick={() => handleSave(logoFile)}
            sx={{
              backgroundColor: '#FFD800',
              color: '#3B4046',
              textTransform: 'none',
              width: 'auto',
              borderRadius: '10px',
              padding: '4px 16px !important',
              '&:hover': {
                backgroundColor: '#FFD800',
              },
            }}
          >
            Save Theme
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default JobBoardLeftCustomizer;
